import PopUpAlert from '@classic/Components/PopUpAlerts/Components/PopUpAlert';
import { instantiate, fillSlot } from '@classic/Shared/TemplateLoader';
import type { PopupAlert } from '@classic/Components/PopUpAlerts/Schemas';
import singleEntryTemplate from '@classic/Components/PopUpAlerts/Components/AlertsDialog.single.html?raw';
import multiEntryTemplate from '@classic/Components/PopUpAlerts/Components/AlertsDialog.multi.html?raw';
import multiEntryItemTemplate from '@classic/Components/PopUpAlerts/Components/AlertsDialog.multiItem.html?raw';

const BODY_SCROLL_LOCK_CLASS = 'modal-open';
const SCROLL_HIDDEN_CLASS = 'hidden';

/**
 * Native `<dialog>` replacement for `Legacy\LightboxModal\Template`'s
 * alert layouts. The single/multi-entry scaffolding lives in
 * `AlertsDialog.*.html` (reusing the existing `.ss-lightbox-modal*` /
 * `.ss-alert-modal-*` Sass — see `classic/Shared/UI/Dialog/Dialog.scss` for
 * the shared browser-default `<dialog>` chrome reset) and the
 * `body.modal-open` scroll-lock rule already defined in `_ss-lightbox.scss`
 * — toggling that class keeps this in sync with any other legacy Bootstrap
 * modal open on the page without new CSS.
 */
class AlertsDialog {
    private readonly dialog: HTMLDialogElement;
    private scrollWrap: HTMLElement | null = null;

    constructor(mount: HTMLElement) {
        this.dialog = document.createElement('dialog');
        this.dialog.id = 'ss-alert-preview';
        this.dialog.className = 'ss-lightbox-modal ss-lightbox-modal-alert';
        this.dialog.addEventListener('close', this.handleClose);
        mount.append(this.dialog);
    }

    public open(alerts: readonly PopupAlert[]): void {
        if (alerts.length === 0) {
            return;
        }

        this.render(alerts);
        document.body.classList.add(BODY_SCROLL_LOCK_CLASS);
        if (!this.dialog.open) {
            this.dialog.showModal();
        }
        this.updateScrollNotifications();
    }

    private readonly handleClose = (): void => {
        document.body.classList.remove(BODY_SCROLL_LOCK_CLASS);
    };

    private render(alerts: readonly PopupAlert[]): void {
        this.dialog.replaceChildren();
        this.dialog.classList.toggle('ss-lightbox-modal-multi-entries', alerts.length > 1);
        this.scrollWrap = null;

        this.dialog.append(
            alerts.length > 1 ? this.buildMultiEntryLayout(alerts) : this.buildSingleEntryLayout(alerts[0]),
        );
    }

    private buildSingleEntryLayout(alert: PopupAlert): DocumentFragment {
        const fragment = instantiate(singleEntryTemplate);
        this.wireCloseButton(fragment);
        fillSlot(fragment, 'entry', this.buildAlertElement(alert));
        return fragment;
    }

    private buildMultiEntryLayout(alerts: readonly PopupAlert[]): DocumentFragment {
        const fragment = instantiate(multiEntryTemplate);
        fillSlot(fragment, 'headerCopy', `${alerts.length} Alerts`);
        this.wireCloseButton(fragment);
        fillSlot(fragment, 'entries', alerts.map((alert) => this.buildMultiEntryContent(alert)));

        this.scrollWrap = fragment.querySelector('.ss-lightbox-modal-content-scroll-wrap');
        this.scrollWrap?.addEventListener('scroll', () => this.updateScrollNotifications());

        return fragment;
    }

    private buildMultiEntryContent(alert: PopupAlert): DocumentFragment {
        const fragment = instantiate(multiEntryItemTemplate);
        fillSlot(fragment, 'entry', this.buildAlertElement(alert));
        return fragment;
    }

    private buildAlertElement(alert: PopupAlert): PopUpAlert {
        const element = new PopUpAlert();
        element.alert = alert;
        return element;
    }

    /** The close button is static markup in both dialog templates; only its behavior is wired up here. */
    private wireCloseButton(root: DocumentFragment): void {
        const button = root.querySelector<HTMLButtonElement>('.ss-close-modal');
        if (!button) {
            return;
        }
        button.autofocus = true;
        button.addEventListener('click', () => this.dialog.close());
    }

    private updateScrollNotifications(): void {
        const scrollWrap = this.scrollWrap;
        if (!scrollWrap) {
            return;
        }

        const isAtBottom = scrollWrap.scrollHeight <= scrollWrap.clientHeight + scrollWrap.scrollTop + 1;
        const isAtTop = scrollWrap.scrollTop === 0;

        this.dialog.querySelector('.lightbox-modal-scroll-up-notification-container')
            ?.classList.toggle(SCROLL_HIDDEN_CLASS, isAtTop);
        this.dialog.querySelector('.lightbox-modal-scroll-down-notification-container')
            ?.classList.toggle(SCROLL_HIDDEN_CLASS, isAtBottom);
    }
}

export default AlertsDialog;
